import Dispatcher from '../Dispatcher';
import Constants from '../Constants';
import Routes from '../../utils/Routes';
import FluxEventEmitter from '../FluxEventEmitter';
import assign from 'object-assign';
import { doFetch } from '../../utils/CommonUtils';

const TileGridActionTypes = Constants.ActionTypes.TileGrid;
const CHANGE_EVENTS = {
	GridData: 'gridDataChange',
	VideoPicturesData: 'videoPicturesDataChange',
	PinnedMediaElement: 'pinnedMediaElement',
};

let _backLinks = {};
let _restoreScrollPosition = false;
let _scrollPositions = {};
let _tileGridData = {};
const _tileGridKey = {};
const _tileGridModes = {};
let _tilePictureData = {};
let _pinBoardPayload = {};

const TileGridStore = assign({}, FluxEventEmitter.prototype, {
	EVENTS: CHANGE_EVENTS,

	clearCache() {
		_backLinks = {};
		_restoreScrollPosition = false;
		_scrollPositions = {};
		_tileGridData = {};
		_tilePictureData = {};
		_pinBoardPayload = {};
	},
	getGridData: function (type, payload) {
		return getData(type, payload);
	},

	hasGridData: function (type, payload) {
		return hasData(type, payload);
	},

	getPictureData: function (albumId) {
		return _tilePictureData[albumId];
	},

	getRestoreScrollPosition() {
		return _restoreScrollPosition;
	},

	getScrollPositionById(tileGridId) {
		return _scrollPositions[tileGridId];
	},

	getTileGridKeyById(tileGridId) {
		return _tileGridKey[tileGridId];
	},

	getTileGridModeById(tileGridId) {
		return _tileGridModes[tileGridId];
	},

	getPinBoardPayload(pinBoardType) {
		return _pinBoardPayload[pinBoardType];
	},
});

function getData(type, payload) {
	const key = getKey(payload);

	return hasData(type, payload) ? _tileGridData[type][key] : null;
}

function getKey(request) {
	const keys = [];
	for (const r in request) {
		if (Object.hasOwn(request, r)) {
			keys.push(r);
		}
	}

	keys.sort();

	const keyObject = {};
	for (let k = 0; k < keys.length; k++) {
		keyObject[keys[k]] = request[keys[k]];
	}

	return JSON.stringify(keyObject);
}

function hasData(type, payload) {
	const key = getKey(payload);
	return typeof _tileGridData[type] !== 'undefined' && typeof _tileGridData[type][key] !== 'undefined';
}

function loadGridData(type, payload, useInfiniteScrolling) {
	if (typeof payload === 'object') {
		doFetch(
			Routes.getRoute(Routes.Names.TILE_GRID, {
				type: type,
				payload: encodeURIComponent(JSON.stringify(payload)),
			}),
			null,
			Constants.HttpMethods.GET,
			true,
			{ cache: 'no-cache' },
		).then(function (result) {
			storeGridData(type, payload, result, useInfiniteScrolling);
		});
	}
}

function storeGridData(type, payload, result, useInfiniteScrolling) {
	const key = getKey(payload);
	let hasChanged = false;
	let error = null;

	let previousKey = null;
	const extendPreviousSet = useInfiniteScrolling && typeof payload.page !== 'undefined' && payload.page > 1;
	if (extendPreviousSet) {
		const payload2 = assign({}, payload, { page: payload.page - 1 });
		previousKey = getKey(payload2);
	}

	if (typeof _tileGridData[type] === 'undefined') {
		_tileGridData[type] = {};
	}
	if (result.total) {
		result.data.total = result.total;
	}
	if (result.data) {
		// merge new data with previous data
		if (extendPreviousSet && typeof _tileGridData[type][previousKey] !== 'undefined') {
			result.data.initialTiles = _tileGridData[type][previousKey].initialTiles.concat(result.data.initialTiles);
		}

		_tileGridData[type][key] = result.data;
		hasChanged = true;
	} else if (result.success === false) {
		hasChanged = true;
		error = 'rest-error';
	}
	if (hasChanged) {
		TileGridStore.emit(CHANGE_EVENTS.GridData, type, error);
	}
}

function loadVideoPicture(albumId) {
	if (!_tilePictureData[albumId]) {
		doFetch(Routes.getRoute(Routes.Names.TILE_GRID_GET_VIDEO_PICTURES, { albumId: albumId }), null, Constants.HttpMethods.GET, true, {
			cache: 'no-cache',
		}).then(function (result) {
			_tilePictureData[albumId] = result;
			TileGridStore.emit(CHANGE_EVENTS.VideoPicturesData, albumId);
		});
	}
}

function setScrollPosition(tileGridId, offset) {
	if (tileGridId) {
		_scrollPositions[tileGridId] = offset;
	}
}

function setBackLink(tileGridId) {
	if (tileGridId) {
		_backLinks[tileGridId] = window.location.href;
	}
}

function resetScrollPosition(tileGridId) {
	if (tileGridId) {
		_scrollPositions[tileGridId] = null;
	}
}

function resetBackLink(tileGridId) {
	if (tileGridId) {
		_backLinks[tileGridId] = null;
	}
}

function setRestoreScrollPosition(restorePosition) {
	_restoreScrollPosition = restorePosition;
}

function setTileGridKey(tileGridId, tileGridKey) {
	if (tileGridId) {
		_tileGridKey[tileGridId] = tileGridKey;
	}
}

function setTileGridMode(tileGridId, gridMode) {
	if (tileGridId) {
		_tileGridModes[tileGridId] = gridMode;
	}
}

TileGridStore.dispatchToken = Dispatcher.register(function (action) {
	switch (action.type) {
		case TileGridActionTypes.LOAD_GRID:
			loadGridData(action.gridType, action.payload, action.useInfiniteScrolling);
			break;
		case TileGridActionTypes.LOAD_VIDEO_ALBUM_PICTURES:
			loadVideoPicture(action.albumId);
			break;
		case TileGridActionTypes.SET_SCROLL_POSITION:
			setScrollPosition(action.tileGridId, action.offset);
			break;
		case TileGridActionTypes.SET_BACK_LINK:
			setBackLink(action.tileGridId);
			break;
		case TileGridActionTypes.RESET_SCROLL_POSITION:
			resetScrollPosition(action.tileGridId);
			break;
		case TileGridActionTypes.RESET_BACK_LINK:
			resetBackLink(action.tileGridId);
			break;
		case TileGridActionTypes.SET_RESTORE_SCROLL_POSITION:
			setRestoreScrollPosition(action.restorePosition);
			break;
		case TileGridActionTypes.SET_TILE_GRID_KEY:
			setTileGridKey(action.tileGridId, action.tileGridKey);
			break;
		case TileGridActionTypes.SET_TILE_GRID_MODE:
			setTileGridMode(action.tileGridId, action.gridMode);
			break;
		default:
	}
});

export default TileGridStore;
